import {Box, SxProps, Theme} from '@mui/material';
import {useCan, usePersona} from 'src/permissions/hooks';
import {FC, useEffect} from 'react';
import {useParams} from 'react-router-dom';
import {chargersApi, usersApi} from 'src/api';
import {useAppSelector} from 'src/app/hooks';
import {store} from 'src/app/store';
import {ActiveCharging} from 'src/components/chargers/ActiveCharging';
import {ChargerDetails} from 'src/components/chargers/ChargerDetails';
import {ChargerSchedule} from 'src/components/chargers/ChargerSchedule';
import {ChargerTransactionsTable} from 'src/components/chargers/ChargerTransactionsTable';
import {PowerConsumption} from 'src/components/common/PowerConsumption';
import {RECORD_ACTION_BAR_HEIGHT} from 'src/components/mobile/RecordActionBar/RecordActionBar.sx';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {ChargePointByIdRequest} from 'src/types';
import {isErrorObject} from 'src/utils/helper';
import {NotifyError} from 'src/utils/notification';
import {chargerInfoSx} from './ChargerInfo.sx';

type ChargersQueryTrigger = ReturnType<typeof chargersApi.useLazyGetChargePointByIdQuery>[0];

const handleGetChargerInfo =
    (trigger: ChargersQueryTrigger) =>
        async (data: ChargePointByIdRequest) => {
            store.dispatch(preloaderChangeStatus(true));

            try {
                const response = await trigger(data).unwrap();
                store.dispatch(preloaderChangeStatus(false));
                return response;
            } catch (error) {
                if (isErrorObject(error)) {
                    NotifyError(error.data.message);
                }
            } finally {
                store.dispatch(preloaderChangeStatus(false));
            }
        };

const pageSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    // Clears the bar above it. 20px left the device card sitting on the bar rather than under it.
    mt: 3.5,
    // The device card's action bar is fixed and reserves nothing where it is rendered, because it is
    // rendered in the middle of this page. The room it needs is here, at the foot, where the last
    // card would otherwise end underneath it.
    pb: {xs: `${RECORD_ACTION_BAR_HEIGHT}px`, lg: 0},
};

/**
 * Read on the left, operate on the right.
 *
 * The rail collapses under the left column on a narrow screen, which is what the second mounted copy
 * of the two panels was there to fake.
 */
const railSx = (hasRail: boolean): SxProps<Theme> => ({
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', lg: hasRail ? '8.5fr 3.5fr' : '1fr'},
    gap: 2,
    alignItems: 'start',
});

const columnSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
};

/**
 * Where a group sits.
 *
 * The page is three groups - the device, what you do with it, what it has done - and on a desktop the
 * first and the third stack in the left column while the second spans both rows on the right, which
 * is the two-column page it has always been. Below `lg` the grid is one column and `order` runs them
 * device → charging and schedule → consumption and sessions, so the two panels people operate the
 * charger with are above the history rather than under it.
 *
 * Each group stays a flex column of its own, so the spacing inside a group and between groups is the
 * same 16px it was.
 */
const groupSx = (order: number, column: string, row: string): SxProps<Theme> => ({
    ...(columnSx as object),
    order,
    gridColumn: {lg: column},
    gridRow: {lg: row},
});

export const ChargerInfo: FC = () => {
    const params = useParams();
    const persona = usePersona();
    const {activeBusiness, authInfo} = useAppSelector((state) => state.common);
    const isCompanyUser = persona.isCompany;
    const isEmployeeUser = persona.isEmployee;
    const isDriverUser = persona.isDriver;
    // A charger can be read by someone who holds nothing about its sessions or schedules - a sales
    // manager. Those blocks are shown to whoever may fetch them rather than fetched into a refusal.
    const canSearchTransactions = useCan('transactions.search');
    const canReadSchedule = useCan('scheduleCharging.read');

    const [trigger, response] = chargersApi.useLazyGetChargePointByIdQuery();

    // Whether there is a second column at all. A charger with no connectors has nothing to charge and
    // nothing to schedule, so the rail is not rendered and the blocks stay one plain column.
    const hasRail = Boolean(response.data?.connectors?.length);

    const [getUserInfo] = usersApi.useLazyGetUserByIdQuery();

    const handleGetUserData = async () => {
        response.data?.user &&
        response.data?.business &&
        (await getUserInfo({
            userId: response.data?.user.id,
            businessId: response.data?.business.id,
        }));
    };

    useEffect(() => {
        void handleGetUserData();
    }, [response]);

    useEffect(() => {
        if (!params?.id) {
            return;
        }

        const data: ChargePointByIdRequest = {chargerId: +params.id, businessId: activeBusiness};

        // No realEstateId or companyId from the account: the server forces whichever of them
        // matches the caller's reach onto the request, and for a caller whose reach is wider
        // they were never theirs to choose.

        void handleGetChargerInfo(trigger)(data);
    }, []);

    return (
        <Box sx={pageSx}>
            {response.data && (
                <Box sx={railSx(hasRail)}>
                    {/* The device. */}
                    <Box sx={groupSx(1, '1', '1')}>
                        <ChargerDetails data={response.data}/>
                    </Box>

                    {/* What you do with it. One render, not two: these were mounted twice - once here
                        and once inside the left column - with the wrong copy hidden by CSS, so every
                        open cost two schedule fetches, two live subscriptions and two copies of their
                        state. On the right on a desktop, directly under the device on a phone, where
                        operating the charger matters more than its history. */}
                    {hasRail && (
                        <Box sx={groupSx(2, '2', '1 / span 2')}>
                            <ActiveCharging data={response.data}/>
                            {canReadSchedule && <ChargerSchedule data={response.data}/>}
                        </Box>
                    )}

                    {/* What it has done. */}
                    <Box sx={groupSx(3, '1', '2')}>
                        <PowerConsumption
                            chargerId={response.data.id}
                            companyId={isCompanyUser ? authInfo?.company?.id : undefined}
                            userId={(isDriverUser || isEmployeeUser) ? authInfo?.id : undefined}
                        />

                        {canSearchTransactions && (
                            <ChargerTransactionsTable
                                chargerId={response.data.id}
                                businessId={response.data.business.id}
                            />
                        )}
                    </Box>
                </Box>
            )}
        </Box>
    );
};
